import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import { useAppearance } from '../contexts/AppearanceContext';

const GuidedTour = ({ isActive, onClose, onComplete }) => {
  const { isSuppleMode } = useAppearance();
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState(null);
  const overlayRef = useRef(null);

  const tourSteps = [
    {
      id: 'context-spaces',
      title: 'Context Spaces',
      description: 'Organize your conversations by topics or projects. Click here to create and manage context spaces with resources like URLs and notes.',
      selector: '[data-tour="context-spaces"]',
      position: 'right'
    },
    {
      id: 'resources-section',
      title: 'Resources',
      description: 'Add websites, documents, and notes to your context space. These resources will be available to the AI during your conversations.',
      selector: '[data-tour="resources-section"]',
      position: 'right'
    },
    {
      id: 'add-resource',
      title: 'Add Resources',
      description: 'Click the + button to add URLs, files, or text notes to your context space.',
      selector: '[data-tour="add-resource"]',
      position: 'right'
    },
    {
      id: 'model-dropdown',
      title: 'Model Selection',
      description: 'Choose from various AI models including GPT-4, Claude, and Gemini. Different models have different capabilities.',
      selector: '[data-tour="model-dropdown"]',
      position: 'bottom'
    },
    {
      id: 'burst-mode',
      title: 'Burst Mode',
      description: 'Get responses from multiple AI models simultaneously for comparison and better insights.',
      selector: '[data-tour="burst-mode"]',
      position: 'bottom'
    },
    {
      id: 'voice-chat',
      title: 'Voice Chat',
      description: 'Have conversations using voice input and output. Perfect for natural, hands-free interactions.',
      selector: '[data-tour="voice-chat"]',
      position: 'top'
    }
  ];

  const currentStepData = tourSteps[currentStep];

  useEffect(() => {
    if (!isActive) return;

    const findAndHighlightElement = () => {
      const element = document.querySelector(currentStepData.selector);
      if (element) {
        setHighlightedElement(element);
        
        // Scroll element into view
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    // Wait a bit for the DOM to be ready
    const timer = setTimeout(findAndHighlightElement, 100);

    return () => clearTimeout(timer);
  }, [currentStep, isActive, currentStepData.selector]);

  // Early return after all hooks are declared
  if (!isActive) return null;

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    setHighlightedElement(null);
    onComplete();
  };

  const handleSkip = () => {
    setHighlightedElement(null);
    onClose();
  };

  const getTooltipPosition = () => {
    if (!highlightedElement) return { top: '50%', left: '50%' };

    const rect = highlightedElement.getBoundingClientRect();
    const tooltipWidth = 320;
    const tooltipHeight = 200;
    
    let top = rect.top;
    let left = rect.left;

    switch (currentStepData.position) {
      case 'right':
        left = rect.right + 16;
        top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
        break;
      case 'left':
        left = rect.left - tooltipWidth - 16;
        top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
        break;
      case 'top':
        top = rect.top - tooltipHeight - 16;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        break;
      case 'bottom':
        top = rect.bottom + 16;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        break;
      default:
        break;
    }

    // Keep tooltip within viewport
    top = Math.max(16, Math.min(top, window.innerHeight - tooltipHeight - 16));
    left = Math.max(16, Math.min(left, window.innerWidth - tooltipWidth - 16));

    return { top: `${top}px`, left: `${left}px` };
  };

  const getHighlightPosition = () => {
    if (!highlightedElement) return {};

    const rect = highlightedElement.getBoundingClientRect();
    return {
      top: `${rect.top - 4}px`,
      left: `${rect.left - 4}px`,
      width: `${rect.width + 8}px`,
      height: `${rect.height + 8}px`
    };
  };

  return (
    <>
      {/* Dark overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" />
      
      {/* Highlight ring */}
      {highlightedElement && (
        <div
          className="fixed z-50 pointer-events-none border-2 border-blue-500 rounded-lg shadow-lg"
          style={getHighlightPosition()}
        />
      )}

      {/* Tooltip */}
      <div
        className={`fixed z-50 w-80 rounded-lg shadow-xl p-4 ${
          isSuppleMode 
            ? 'supple-card-glass backdrop-blur-lg supple-border-subtle' 
            : 'bg-white border border-gray-200'
        }`}
        style={getTooltipPosition()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              isSuppleMode ? 'supple-btn-gradient-blue text-white' : 'bg-blue-600 text-white'
            }`}>
              {currentStep + 1}
            </div>
            <h3 className={`font-semibold ${
              isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
            }`}>
              {currentStepData.title}
            </h3>
          </div>
          <button
            onClick={handleSkip}
            className={`p-1 rounded transition-colors ${
              isSuppleMode 
                ? 'supple-text-muted hover:supple-text-primary' 
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className={`text-sm mb-4 ${
          isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
        }`}>
          {currentStepData.description}
        </p>

        <div className="flex items-center justify-between">
          <div className="flex space-x-1">
            {tourSteps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full ${
                  index === currentStep
                    ? 'bg-blue-600'
                    : isSuppleMode
                      ? 'bg-gray-300'
                      : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className={`px-3 py-1.5 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isSuppleMode 
                  ? 'supple-text-muted hover:supple-text-primary hover:supple-card-glass-hover' 
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {currentStep === tourSteps.length - 1 ? (
              <button
                onClick={handleComplete}
                className={`px-4 py-1.5 text-sm rounded transition-colors ${
                  isSuppleMode 
                    ? 'supple-btn-gradient-blue text-white hover:supple-btn-gradient-blue-hover' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Finish
              </button>
            ) : (
              <button
                onClick={handleNext}
                className={`px-4 py-1.5 text-sm rounded transition-colors ${
                  isSuppleMode 
                    ? 'supple-btn-gradient-blue text-white hover:supple-btn-gradient-blue-hover' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Next <ChevronRight className="w-4 h-4 inline ml-1" />
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GuidedTour;